import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { logDataModels } from './dataModelsLogger';
import type { TableColumn } from './dataModelsTypes';
import type { Classification } from './governance/types';

const STORAGE_KEY = 'datavault.schema.classifications.v1';
const SENSITIVE_CLASSES: Classification[] = ['Restricted', 'PII', 'PHI', 'PCI'];

export type ColumnClassificationMap = Record<string, Classification | null>;

export function classificationKey(tableId: string, columnName: string): string {
  return `${tableId}:${columnName}`;
}

export function applyColumnClassification(
  column: TableColumn,
  tableId: string,
  overrides?: ColumnClassificationMap,
): TableColumn {
  if (!overrides) return column;
  const key = classificationKey(tableId, column.name);
  if (!(key in overrides)) return column;
  const next = overrides[key];
  return {
    ...column,
    classification: next ?? undefined,
    pii: next === 'PII',
  };
}

export function applyColumnClassifications(
  columns: TableColumn[],
  tableId: string,
  overrides?: ColumnClassificationMap,
): TableColumn[] {
  if (!overrides) return columns;
  return columns.map((column) => applyColumnClassification(column, tableId, overrides));
}

function loadOverrides(): ColumnClassificationMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as ColumnClassificationMap;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    logDataModels('warning', 'schema_classification_store_load_failed', { key: STORAGE_KEY });
    return {};
  }
}

function persistOverrides(map: ColumnClassificationMap) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    logDataModels('warning', 'schema_classification_store_persist_failed', { key: STORAGE_KEY });
  }
}

interface ColumnClassificationValue {
  overrides: ColumnClassificationMap;
  setClassification: (tableId: string, columnName: string, value: Classification | null) => void;
  saveOverrides: (patch: ColumnClassificationMap) => void;
}

const ColumnClassificationContext = createContext<ColumnClassificationValue | null>(null);

export function ColumnClassificationProvider({ children }: { children: React.ReactNode }) {
  const [overrides, setOverrides] = useState<ColumnClassificationMap>(loadOverrides);

  const logChange = (tableId: string, columnName: string, value: Classification | null) => {
    logDataModels('app', 'schema_column_classification_set', { tableId, column: columnName, classification: value ?? '' });
    if (value && SENSITIVE_CLASSES.includes(value)) {
      logDataModels('warning', 'schema_column_classification_sensitive', { tableId, column: columnName, classification: value });
    }
  };

  const setClassification = useCallback((tableId: string, columnName: string, value: Classification | null) => {
    const key = classificationKey(tableId, columnName);
    setOverrides((current) => {
      const next = { ...current, [key]: value };
      persistOverrides(next);
      return next;
    });
    logChange(tableId, columnName, value);
  }, []);

  const saveOverrides = useCallback((patch: ColumnClassificationMap) => {
    setOverrides((current) => {
      const next = { ...current, ...patch };
      persistOverrides(next);
      return next;
    });
    Object.entries(patch).forEach(([key, value]) => {
      const split = key.indexOf(':');
      logChange(key.slice(0, split), key.slice(split + 1), value);
    });
    logDataModels('app', 'schema_column_classifications_saved', { count: Object.keys(patch).length });
  }, []);

  const value = useMemo<ColumnClassificationValue>(
    () => ({ overrides, setClassification, saveOverrides }),
    [overrides, saveOverrides, setClassification],
  );

  return <ColumnClassificationContext.Provider value={value}>{children}</ColumnClassificationContext.Provider>;
}

export function useColumnClassifications(): ColumnClassificationValue {
  const value = useContext(ColumnClassificationContext);
  if (!value) throw new Error('Column classification is only available inside Data models.');
  return value;
}
